import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getProduct, reset } from "../features/products/productSlice";
import { addToCart } from "../features/cart/cartSlice";
import Loader from "../components/Loader";
import { useToast } from "../components/Toast";
import {
  HiArrowLeft,
  HiOutlineShoppingBag,
  HiOutlineLightningBolt,
  HiOutlineShieldCheck,
} from "react-icons/hi";

function ProductDetail() {
  const toast = useToast();
  const { code } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [quantity, setQuantity] = useState(1);

  const { product, isLoading, isError, message } = useSelector(
    (state) => state.products
  );
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(getProduct(code));
    return () => {
      dispatch(reset());
    };
  }, [dispatch, code]);

  const handleAddToCart = () => {
    if (!user) {
      toast.info("Please login to initiate acquisition");
      navigate("/login");
      return;
    }

    // Adding multiple times if quantity > 1
    for (let i = 0; i < quantity; i++) {
      dispatch(addToCart({ email: user.data.email, item: product }));
    }
    toast.success(
      `${product.name || product.title} added to arsenal inventory!`
    );
  };

  if (isLoading)
    return (
      <div className="h-screen flex items-center justify-center bg-[#0b0e14]">
        <Loader />
      </div>
    );
  if (isError)
    return (
      <div className="h-screen flex items-center justify-center bg-[#0b0e14] text-red-500 font-black uppercase tracking-widest italic">
        {message || "SYSTEM_ERROR: PRODUCT_NOT_FOUND"}
      </div>
    );
  if (!product) return null;

  return (
    <div className="min-h-screen bg-[#0b0e14] py-20 px-4 md:px-8 relative overflow-hidden">
      {/* Background Glows */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10 w-full">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="group mb-12 flex items-center gap-3 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl transition-all text-gray-400 hover:text-cyan-400 font-black uppercase text-[10px] tracking-[0.2em] italic"
        >
          <HiArrowLeft className="text-lg group-hover:-translate-x-1 transition-transform" />
          RETURN_TO_ARSENAL
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Hardware Visualization */}
          <div className="relative group">
            {/* Ambient Shadow/Glow */}
            <div className="absolute inset-0 bg-cyan-500/20 rounded-[3rem] blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

            <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[3rem] p-12 flex items-center justify-center relative overflow-hidden shadow-2xl">
              {/* Cyber Accents */}
              <div className="absolute top-8 left-8 w-12 h-12 border-t-2 border-l-2 border-cyan-500/30 rounded-tl-2xl" />
              <div className="absolute bottom-8 right-8 w-12 h-12 border-b-2 border-r-2 border-cyan-500/30 rounded-br-2xl" />

              <img
                src={product.image}
                alt={product.name || product.title}
                className="max-h-[550px] object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)] scale-95 hover:scale-100 transition-transform duration-700"
              />

              {/* Holographic Label */}
              <div className="absolute bottom-10 left-10 flex items-center gap-3 px-4 py-2 bg-black/40 backdrop-blur-md border border-white/10 rounded-xl">
                <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-ping" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">
                  Live Capture System
                </span>
              </div>
            </div>
          </div>

          {/* Specification Details */}
          <div className="flex flex-col h-full pt-4">
            <div className="mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/10 border border-purple-500/20 rounded-full text-[10px] font-black text-purple-400 uppercase tracking-widest mb-4">
                Hardware Class: {product.category || "PREMIUM_GEAR"}
              </div>
              <h1 className="text-5xl lg:text-7xl font-black text-white uppercase italic tracking-tighter mb-4 leading-none">
                {product.name || product.title}
              </h1>
              <p className="text-cyan-400 font-black text-sm uppercase tracking-[0.3em] flex items-center gap-3">
                <span className="w-8 h-px bg-cyan-500"></span>
                {product.color
                  ? `${product.color} Edition`
                  : "Alpha Deployment Package"}
              </p>
            </div>

            <div className="mb-12 flex items-end gap-6">
              <div className="space-y-1">
                <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                  Credits Required
                </p>
                <h2 className="text-5xl font-black text-white italic tracking-tighter">
                  ${product.price}
                </h2>
              </div>
              <div className="pb-1">
                <p className="text-sm text-gray-400 line-through font-bold">
                  ${(product.price * 1.5).toFixed(2)}
                </p>
                <span className="text-[10px] font-black text-red-500 bg-red-500/10 px-2 py-0.5 rounded border border-red-500/20 uppercase tracking-widest mt-1 inline-block">
                  -33% EFFICIENCY
                </span>
              </div>
            </div>

            <div className="mb-12 space-y-6">
              <h3 className="text-[11px] font-black text-white uppercase tracking-[0.3em] flex items-center gap-3">
                INTELLIGENCE_FILE{" "}
                <span className="flex-1 h-px bg-white/10"></span>
              </h3>
              <p className="text-gray-400 leading-relaxed font-medium text-lg">
                High-performance {product.name || "unit"} engineered for the
                elite. Optimized for low-latency response and maximum industrial
                endurance. Integrating the latest haptic and visual advancements
                for an unfair advantage in the field.
              </p>
            </div>

            <div className="flex flex-col gap-6 mt-auto">
              <div className="flex items-center gap-6">
                <div className="flex items-center bg-white/5 border border-white/10 rounded-2xl p-1">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 flex items-center justify-center text-xl text-gray-400 hover:text-white transition-colors"
                  >
                    -
                  </button>
                  <span className="w-12 text-center font-black text-white">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 flex items-center justify-center text-xl text-gray-400 hover:text-white transition-colors"
                  >
                    +
                  </button>
                </div>

                <p className="text-[11px] font-black text-gray-500 uppercase tracking-widest italic">
                  Units per Deployment
                </p>
              </div>

              <button
                onClick={handleAddToCart}
                className="group relative w-full p-6 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-[2rem] text-white font-black uppercase tracking-[0.2em] italic text-sm hover:from-cyan-400 hover:to-purple-500 transition-all duration-300 shadow-2xl shadow-cyan-500/20 active:scale-[0.98] overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                <span className="relative flex justify-center items-center gap-3">
                  <HiOutlineShoppingBag className="text-2xl" />
                  INITIATE_ACQUISITION
                </span>
              </button>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-6 bg-white/5 rounded-[2rem] border border-white/10 group hover:border-cyan-500/30 transition-all">
                <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 italic">
                  Dispatch Matrix
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-cyan-500/10 rounded-lg flex items-center justify-center text-cyan-400">
                    <HiOutlineLightningBolt />
                  </div>
                  <p className="text-xs font-black text-white uppercase italic">
                    Instant Deployment Ready
                  </p>
                </div>
              </div>
              <div className="p-6 bg-white/5 rounded-[2rem] border border-white/10 group hover:border-purple-500/30 transition-all">
                <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 italic">
                  Warranty Protocol
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center text-purple-400">
                    <HiOutlineShieldCheck />
                  </div>
                  <p className="text-xs font-black text-white uppercase italic">
                    Full Matrix Coverage
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;
